import React, { useState } from 'react';
import { Ambulance, MapPin, Clock, CreditCard, Info } from 'lucide-react';
import { emergencyPrices } from '../mock';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const EmergencyPrices = () => {
  const [selectedCity, setSelectedCity] = useState(emergencyPrices.cities[0].name);

  const currentCity = emergencyPrices.cities.find(city => city.name === selectedCity);

  return (
    <section id="emergency-prices" className="py-20 bg-gradient-to-br from-red-50 via-white to-orange-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-block mb-4">
            <span className="bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-semibold flex items-center space-x-2">
              <Ambulance className="h-4 w-4" />
              <span>24/7 Notdienst</span>
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
              Notfall-Pauschalpreise
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Transparente Festpreise für Notfall-Transporte zu den Tierkliniken
          </p>
        </div>

        {/* City Selector */}
        <div className="mb-8">
          <Card className="shadow-lg border-2 border-red-200">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
                <div className="flex items-center space-x-2 text-gray-700">
                  <MapPin className="h-5 w-5 text-red-600" />
                  <span className="font-semibold">Wählen Sie Ihre Stadt:</span>
                </div>
                <Select value={selectedCity} onValueChange={setSelectedCity}>
                  <SelectTrigger className="sm:w-[400px]">
                    <SelectValue placeholder="Stadt auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {emergencyPrices.cities.map((city) => (
                      <SelectItem key={city.name} value={city.name}>
                        {city.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Price Table */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {currentCity.clinics.map((clinic, index) => (
            <Card
              key={clinic.name}
              className="shadow-xl border-2 border-gray-200 hover:border-red-400 hover:shadow-2xl transition-all duration-300"
            >
              <CardHeader className="bg-gradient-to-br from-red-50 to-orange-50 border-b-2 border-gray-200">
                <CardTitle className="text-xl font-bold text-gray-900 flex items-center space-x-2">
                  <Ambulance className="h-5 w-5 text-red-600" />
                  <span>{clinic.name}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="text-center mb-4">
                  <div className="text-5xl font-bold text-transparent bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text">
                    {clinic.price}€
                  </div>
                  <div className="text-sm text-gray-600 mt-2">Pauschalpreis</div>
                </div>

                <div className="space-y-2 text-sm text-gray-700">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                    <span>Hin- und Rückweg</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                    <span>1h Wartezeit (Tag)</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                    <span>2h Wartezeit (Nacht)</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Info Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Waiting Service Info */}
          <Card className="shadow-lg border-2 border-gray-200">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100">
              <CardTitle className="flex items-center space-x-2 text-lg">
                <Clock className="h-5 w-5 text-blue-600" />
                <span>Warteservice-Details</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Tagsüber (07:00-19:00 Uhr)</h4>
                <p className="text-sm text-gray-700">
                  <strong>Inklusive:</strong> {emergencyPrices.waitingService.day.included}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Zusätzlich:</strong> {emergencyPrices.waitingService.day.additional}
                </p>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-semibold text-gray-900 mb-2">Nachts (19:00-07:00 Uhr)</h4>
                <p className="text-sm text-gray-700">
                  <strong>Inklusive:</strong> {emergencyPrices.waitingService.night.included}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Zusätzlich:</strong> {emergencyPrices.waitingService.night.additional}
                </p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg mt-4">
                <p className="text-xs text-gray-600">
                  Der Warteservice ist bei Notfall-Pauschalpreisen nicht optional, da ansonsten der Zeitaufwand sowie die Fahrtkosten nicht tragbar wären.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Payment Options */}
          <Card className="shadow-lg border-2 border-gray-200">
            <CardHeader className="bg-gradient-to-r from-orange-50 to-orange-100">
              <CardTitle className="flex items-center space-x-2 text-lg">
                <CreditCard className="h-5 w-5 text-orange-600" />
                <span>Zahlungsoptionen</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-start space-x-3">
                <div className="bg-orange-100 p-2 rounded-lg">
                  <CreditCard className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Ratenzahlung möglich</h4>
                  <p className="text-sm text-gray-700">
                    {emergencyPrices.paymentOptions}
                  </p>
                </div>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2 text-sm">Kurzstrecken im Notdienst</h4>
                <p className="text-xs text-gray-700 mb-2">
                  Normale km-Berechnung (2,20€/km, Rentner 1,80€/km)
                </p>
                <p className="text-xs text-gray-700">
                  <strong>+ 20€ Notfallpauschale</strong> (19:00-07:00 Uhr)
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Important Notice */}
        <div className="mt-8 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-6 text-white">
          <div className="flex items-start space-x-4">
            <Info className="h-8 w-8 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-xl font-bold mb-2">Wichtige Information</h3>
              <p className="text-sm opacity-90 mb-3">
                Im Notfall erreichen Sie uns rund um die Uhr unter <strong className="text-lg">01573 969 69 42</strong>
              </p>
              <p className="text-sm opacity-90">
                Falls ich beim ersten Anruf nicht wach werde, rufen Sie ruhig mehrmals an – im Notfall zählt jede Minute!
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EmergencyPrices;
